import useDocumentTitle from "hooks/useDocumentTitle";
import { TabPanel, TabView } from "primereact/tabview";
import { useSearchParams } from "react-router-dom";

import { BenefitComparisonTab } from "./BenefitComparisonTab";
import { LineTypesTab } from "./LineTypesTab";
import { PayerOverridesTab } from "./PayerOverridesTab";
import { RemitAccuracyTab } from "./RemitAccuracyTab";

/**
 * CLE-2792 — Eligibility: the payer-agnostic settings behind every eligibility check.
 *
 * A TABBED page. The tabs are not decoration: line types and payer overrides both belong to
 * eligibility as a whole rather than to one account or one payer, and giving the first one a page of
 * its own would have meant moving it when the second arrived — which it now has.
 *
 * Payer overrides come first and open by default (Mike, 2026-09-14): they are the tab edited day
 * to day — a payer reporting the wrong copay is a recurring event — while a line type definition
 * is written once per payer wording and then left alone. The engine reads them in the other order
 * (line selection first, then the override on top of it); the order the engine runs them is not
 * the order a person needs them.
 *
 * CLE-2820 — SUPER ADMIN, and nothing narrower. The page used to hide itself from every super admin
 * but two named addresses while the engine was mid-cutover, and returned `null` for everyone else —
 * a blank screen at a real route. The route is `WithRoleProtection allowedRoles={[SUPER_ADMIN]}` and
 * every endpoint these tabs call requires SuperAdmin, so the body simply renders: whoever got here
 * is allowed to be here, and the API is the boundary either way.
 *
 * That now includes the fourth tab, "Engine worklist". It was shown to one named address while its
 * owner iterated on it (a `COMPARISON_TAB_PREVIEW_EMAILS` list, 2026-09-17); on 2026-09-28 Mike opened
 * it to every super admin and the list was deleted. Every tab is now the whole SuperAdmin role — the
 * route's `WithRoleProtection allowedRoles={[SUPER_ADMIN]}` — and nothing narrower. If a tab ever
 * needs narrowing again, that is a permission, not an email list.
 *
 * CLE-2820 follow-up — that fourth tab is now "Engine worklist". "Legacy vs Engine vs AI" named its
 * INPUTS; this names the JOB. Remit accuracy stays the measurement tab next door; this one is where
 * you go to fix things. Its `?tab=` value moved with it, from `legacy-engine-ai` to `engine-worklist`
 * — an old link opens the first tab, which is what every unknown value already did.
 */
/** The `?tab=` value of each tab, in tab order. An unknown or missing value opens the first. */
export const ELIGIBILITY_TABS = [
  "payer-overrides",
  "line-types",
  "remit-accuracy",
  "engine-worklist",
] as const;

export function EligibilityPage() {
  useDocumentTitle("Eligibility");
  const [params, setParams] = useSearchParams();

  const activeIndex = Math.max(
    0,
    ELIGIBILITY_TABS.indexOf(
      params.get("tab") as (typeof ELIGIBILITY_TABS)[number]
    )
  );

  // A tab change PUSHES a history entry, so Back returns to the previous tab. The other tabs' own params
  // (the remit accuracy filters) are dropped: they belong to the tab being left.
  const changeTab = (index: number) =>
    setParams({ tab: ELIGIBILITY_TABS[index] });

  return (
    <div className="p-4" data-testid="eligibilityPage">
      <h2 className="m-0 mb-3">Eligibility</h2>
      <TabView
        data-testid="eligibilityPage.tabs"
        activeIndex={activeIndex}
        onTabChange={(event) => changeTab(event.index)}
      >
        <TabPanel header="Payer overrides">
          <PayerOverridesTab />
        </TabPanel>
        <TabPanel header="Line types">
          <LineTypesTab />
        </TabPanel>
        {/* CLE-2818 — internal measurement of the engine against 835 remits. */}
        <TabPanel header="Remit accuracy">
          <RemitAccuracyTab />
        </TabPanel>
        {/*
          CLE-2820 — the Engine worklist: the scorecard, the coverage strip and one ranked list of
          clusters to fix. Last, and open to every super admin like the other three.
        */}
        <TabPanel header="Engine worklist">
          <BenefitComparisonTab />
        </TabPanel>
      </TabView>
    </div>
  );
}
